import React, { useEffect, useState } from "react";
import {
  Box,
  TextField,
  Button,
  Grid,
  Switch,
  FormControlLabel,
  Typography,
  MenuItem
} from "@mui/material";
import { get, post } from "src/services/httpClient.service";
import { useSnackbar } from "src/components/snackbar/useSnackbar";

/* =========================
   Types
========================= */
interface Vendor {
  vendorId: string;
  vendorName: string;
}

interface CityDropdown {
  id: number;
  city: string;
}

interface AddAreaProps {
  handleClose: () => void;
  fetchAreas: () => void;
  selectedVendorId: string;
}

const BoardAddArea: React.FC<AddAreaProps> = ({
  handleClose,
  fetchAreas,
  selectedVendorId
}) => {
  const APIURL = process.env.REACT_APP_BASE_API_URL;
  const { showSnackbar } = useSnackbar();

  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [cities, setCities] = useState<CityDropdown[]>([]);
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    vendorId: selectedVendorId || "",
    cityId: "",
    name: "",
    // zipCode: "",
    isActive: true
  });

  /* =========================
     Fetch Vendors (for display purposes only)
  ========================= */
  useEffect(() => {
    const fetchVendors = async () => {
      try {
        const res = await get(`${APIURL}/api/vendors`);
        setVendors(res?.data || res);
      } catch {
        showSnackbar("Failed to load vendors", "error");
      }
    };

    fetchVendors();
  }, [APIURL, showSnackbar]);

  /* =========================
     Update form when selectedVendorId prop changes
  ========================= */
  useEffect(() => {
    setForm(prev => ({ ...prev, vendorId: selectedVendorId }));
  }, [selectedVendorId]);

  /* =========================
     Fetch Cities (Vendor Based)
  ========================= */
  useEffect(() => {
    const fetchCities = async () => {
      if (!selectedVendorId) {
        setCities([]);
        return;
      }

      try {
        const response = await get(
          `${APIURL}/api/miscellaneous/city/vendor/${selectedVendorId}/findAll`
        );
        setCities(response?.data || response || []);
      } catch {
        showSnackbar("Failed to load cities", "error");
      }
    };

    fetchCities();
  }, [APIURL, selectedVendorId, showSnackbar]);

  /* =========================
     Handlers
  ========================= */
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    if (!form.vendorId || !form.cityId || !form.name ) {
      showSnackbar(
        "Vendor, City, Area Name and Zip Code are required",
        "warning"
      );
      return;
    }

    setLoading(true);

    try {
      const payload = {
        id: 0,
        vendorId: form.vendorId,
        cityId: Number(form.cityId),
        name: form.name,
        // zipCode: form.zipCode,
        isActive: form.isActive
      };

      const response = await post(
        `${APIURL}/api/miscellaneous/area`,
        payload
      );

      if (response?.statusCode === 200) {
        showSnackbar(
          response?.message || "Area added successfully",
          "success"
        );
        fetchAreas();
        handleClose();
      } else {
        showSnackbar(
          response?.message || "Failed to add area",
          "error"
        );
      }
    } catch (error: any) {
      showSnackbar(error?.message || "Failed to add area", "error");
    } finally {
      setLoading(false);
    }
  };

  const selectedVendor = vendors.find(v => v.vendorId === selectedVendorId);

  /* =========================
     UI
  ========================= */
  return (
    <Box p={3}>
      <Typography variant="h6" mb={2}>
        Add Area
      </Typography>

      <Grid container spacing={2}>
        {/* Vendor Display - Read Only */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            required
            label="Vendor"
            value={selectedVendor?.vendorName || selectedVendorId || ""}
            disabled
            InputProps={{
              readOnly: true,
            }}
            helperText="Vendor is selected from the parent list"
          />
          <input type="hidden" name="vendorId" value={form.vendorId} />
        </Grid>

        {/* City Dropdown */}
        <Grid item xs={12}>
          <TextField
            select
            fullWidth
            required
            label="City"
            name="cityId"
            value={form.cityId}
            onChange={handleChange}
            disabled={!selectedVendorId}
          >
            <MenuItem value="" disabled>
              Select City
            </MenuItem>
            {cities.map(city => (
              <MenuItem key={city.id} value={city.id}>
                {city.city}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        {/* Area Name */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            required
            label="Area Name"
            name="name"
            value={form.name}
            onChange={handleChange}
          />
        </Grid>

        {/* Zip Code */}
        {/* <Grid item xs={12}>
          <TextField
            fullWidth
            required
            label="Zip Code"
            name="zipCode"
            value={form.zipCode}
            onChange={handleChange}
          />
        </Grid> */}

        {/* Active Switch */}
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={form.isActive}
                onChange={e =>
                  setForm(prev => ({
                    ...prev,
                    isActive: e.target.checked
                  }))
                }
              />
            }
            label="Is Active"
          />
        </Grid>

        {/* Actions */}
        <Grid item xs={12} display="flex" justifyContent="flex-end" gap={2}>
          <Button
            variant="outlined"
            onClick={handleClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading}
          >
            Save
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default BoardAddArea;